import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {Box, Typography} from '@mui/material';

import {TableRowRequired} from '../../../../../components/BasicTable/BasicTable';
import {BasicTableCell, BasicTableRow} from '../../../../../components/BasicTable/BasicTableRow';
import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import {TextOverflowWithTooltip} from '../../../../../components/TextOverflowWithTooltip/TextOverflowWithTooltip';
import {AppConfig} from '../../../../../lib/AppConfig';
import {transformDate} from '../../../../../utils/dateUtils/dateFormattingUtils';
import {getMatchStatusColor} from '../../../../../utils/matchUtils';
import {MatchStatusLabel} from '../../../../company/recruiting/talent-pool/MatchStatusLabel';
import {Match} from '../../types/ApplicantMatchesTypes';

const CELL_TEXT_SX = {fontSize: '13px', letterSpacing: '0.39px'} as const;

/** Full cell width with fixed height so the action button matches the company matches table. */
const MATCH_TABLE_ACTION_BUTTON_SX = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  height: 32,
  minHeight: 32,
  maxHeight: 32,
  boxSizing: 'border-box',
  py: 0,
  px: 0.75,
  borderRadius: '5px',
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: '16px',
  textTransform: 'none' as const,
  whiteSpace: 'nowrap',
  '&&.MuiButton-contained, &&.MuiButton-outlined': {
    minHeight: 32,
    maxHeight: 32,
    paddingTop: 0,
    paddingBottom: 0,
  },
};

export const ApplicantMatchRow = (match: Match & TableRowRequired) => {
  const {t} = useTranslation('translation', {keyPrefix: 'applicantDashboard.matchesView'});
  const navigate = useNavigate();

  const matchDetailsTo = `${AppConfig.APPLICANT_MATCHES_PATH}/${match.resourceId}`;
  const redirect = () => navigate(matchDetailsTo, {state: {match}});

  const dotColor = getMatchStatusColor(match.status);
  const city = match.companyJobPosting.locations[0]?.city ?? '—';
  const salary = match.companyJobPosting.salary;
  const salaryLabel = salary?.min || salary?.max ? `${salary.min} - ${salary.max}` : '—';

  return (
    <BasicTableRow rowVariant='flat'>
      <BasicTableCell>
        <TextOverflowWithTooltip
          variant='body2'
          sxTypography={CELL_TEXT_SX}
          text={match.company.name}
          to={matchDetailsTo}
        />
      </BasicTableCell>
      <BasicTableCell>
        <TextOverflowWithTooltip
          variant='body2'
          sxTypography={CELL_TEXT_SX}
          text={match.companyJobPosting.title || '—'}
        />
      </BasicTableCell>
      <BasicTableCell>
        <TextOverflowWithTooltip variant='body2' sxTypography={CELL_TEXT_SX} text={city} />
      </BasicTableCell>
      <BasicTableCell>
        <TextOverflowWithTooltip variant='body2' sxTypography={CELL_TEXT_SX} text={salaryLabel} />
      </BasicTableCell>
      <BasicTableCell>
        <Typography sx={CELL_TEXT_SX}>{match.culturalFitScore || '—'}</Typography>
      </BasicTableCell>
      <BasicTableCell>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0}}>
          <Box sx={{width: 10, height: 10, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0}} />
          <Typography sx={{...CELL_TEXT_SX, lineHeight: '16px', textTransform: 'capitalize', minWidth: 0}} noWrap>
            <MatchStatusLabel status={match.status} />
          </Typography>
        </Box>
      </BasicTableCell>
      <BasicTableCell>
        <Typography sx={CELL_TEXT_SX}>
          {match.createdAt ? transformDate(match.createdAt, 'numeric') : '—'}
        </Typography>
      </BasicTableCell>
      <BasicTableCell align='right' sx={{minWidth: 0, overflow: 'hidden', verticalAlign: 'middle'}}>
        <EmpionButton
          size='small'
          variant='secondary'
          disableElevation
          onClick={redirect}
          sx={MATCH_TABLE_ACTION_BUTTON_SX}
        >
          {t('viewMore')}
        </EmpionButton>
      </BasicTableCell>
    </BasicTableRow>
  );
};
